import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { debounceTime, finalize, forkJoin, Observable, Subject } from 'rxjs';

// Components
import { TableComponent } from "../../../shared/components/table/table.component";
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { FileDownloadComponent } from '../file-download/file-download.component';

// Models
import { Column } from '../../../shared/models/column.model';
import { MultiAction } from '../../../shared/models/multi-action.model';
import { PaginatedDocumentResponse, Pagination } from '../../../shared/models/pagination.model';
import { ContextDocument } from '../../models/document.model';

// Services
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DocumentService } from '../../services/document.service';
import { ToastService } from '../../../shared/services/toast.service';
import { FcmService } from '../../../shared/services/fcm.service';


@Component({
  selector: 'app-document-list',
  imports: [AsyncPipe, TableComponent],
  templateUrl: './document-list.component.html'
})
export class DocumentListComponent implements OnInit, OnChanges {
  private readonly docSrv = inject(DocumentService);
  private readonly modalSrv = inject(NgbModal);
  private readonly toastSrv = inject(ToastService);
  private readonly fcmSrv = inject(FcmService);

  @Input() contextId!: string;
  @Input() searchTerm!: string;

  private readonly searchTermSubject = new Subject<string>();

  multiActions: MultiAction[] = [
    { label: $localize`Aggiungi Documenti`, action: 'add', buttonType: 'primary' },
    { label: $localize`Elimina`, action: 'delete', buttonType: 'danger' }
  ];

  pagination: Pagination = {
    page: 1,
    pageSize: 5,
    sortColumn: '',
    sortDirection: ''
  };

  files$!: Observable<PaginatedDocumentResponse<any>>;
  loading: boolean = false;

  ngOnInit(): void {
    this.loadData();
    this.searchTermSubject.pipe(debounceTime(3000)).subscribe(() => this.loadData(this.searchTerm));
    this.fcmSrv.message$.subscribe(() => this.loadData());
    this.fcmSrv.registerServiceWorker(this.contextId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchTerm'] && !changes['searchTerm'].firstChange) {
      this.loading = true;
      this.searchTermSubject.next(this.searchTerm);
    }
  }

  updatePagination(newPage: number): void {
    this.pagination.page = newPage;
    this.loadData();
  }

  updateSort(sortEvent: { column: string, direction: string }): void {
    this.pagination.sortColumn = sortEvent.column;
    this.pagination.sortDirection = sortEvent.direction;
    this.loadData();
  }

  fileColumns: Column[] = [
    { label: 'Id', key: 'id', type: 'hidden' },
    { label: $localize`Tipo`, key: 'contentType', type: 'file-icon' },
    { label: $localize`Nome`, key: 'name', type: 'string', fixedSize: true },
    { label: $localize`Stato`, key: 'status', type: 'status' },
    { label: $localize`Inserito il`, key: 'createTime', type: 'date' },
    { label: $localize`Caricato da`, key: 'user', type: 'string' },
    { label: $localize`Dimensione`, key: 'size', type: 'byte' },
    { label: '', key: '', type: 'action', actionType: 'download' }
  ];

  loadData(searchTerm: string = ''): void {
    this.loading = true;
    this.files$ = this.docSrv.getContextDocumentsList(this.contextId, this.pagination, searchTerm).pipe(
      finalize(() => {
        this.loading = false;
      })
    );
  }

  openUploadModal() {
    const modalRef = this.modalSrv.open(FileUploadComponent, { centered: true, size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.contextId = this.contextId;
    modalRef.result.then(
      (result) => {
        console.log(`Closed with: ${result}`);
      },
      (reason) => {
        this.loadData();
        console.log(`Closed with: ${reason}`);
      },
    );
  }

  openDocumentPreview(doc: ContextDocument): void {
    const modalRef = this.modalSrv.open(FileDownloadComponent, { size: 'lg', centered: true, windowClass: 'qai-modal' });
    modalRef.componentInstance.downloadingFile = doc;
  }

  deleteDocuments(documents: ContextDocument[]): void {
    // filtra i documenti con stato 'indexing' o 'deleting'
    const invalidDocs = documents.filter(doc => doc.status === 'INDEXING' || doc.status === 'DELETING');
    if (invalidDocs.length > 0) {
      this.toastSrv.show({ type: 'warning', message: $localize`Non è possibile eliminare i documenti mentre sono in fase di indicizzazione o già in eliminazione.` });
      return;
    }

    const modalRef = this.modalSrv.open(ConfirmModal, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.componentInstance.title = 'Elimina Documenti';
    modalRef.componentInstance.body = 'Stai eliminando i Documenti:';
    modalRef.componentInstance.list = documents.map(el => el.name);
    modalRef.result.then(
      (result) => {
        this.apiDeleteDocuments(documents);
        console.log(`Closed with: ${result}`);
      },
      (reason) => {
        console.log(`Dismissed`);
      },
    );
  }

  private apiDeleteDocuments(documents: ContextDocument[]) {
    const ids = documents.map(doc => doc.id);

    this.docSrv.deleteFilesBulk(ids)
      .pipe(
        finalize(() => {
          this.loadData();
        })
      )
      .subscribe({
        next: (response) => {
          if (response.totalFailed === 0) {
            this.toastSrv.show({
              type: 'success',
              message: $localize`Documenti eliminati con successo (${response.totalSuccess})`
            });
          } else {
            this.toastSrv.show({
              type: 'warning',
              message: $localize`
              Eliminazione completata parzialmente.
              Successi: ${response.totalSuccess},
              Errori: ${response.totalFailed}
            `
            });

            console.warn('Documenti non eliminati:', response.failed);
          }
        },
        error: (error) => {
          console.error('Error during bulk documents deletion:', error);
        }
      });
  }
}
